import type { DateString, ID } from '../common';

export const USER_ROLE = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  EMPLOYEE: 'employee',
} as const;

export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

/**
 * Check if a user has the platform-admin role
 */
export const isAdmin = (user: User | null): boolean => {
  return user?.role === USER_ROLE.ADMIN;
};

/**
 * Check if a user has the manager role
 */
export const isManager = (user: User | null): boolean => {
  return user?.role === USER_ROLE.MANAGER;
};

/**
 * Check if a user has the employee role
 */
export const isEmployee = (user: User | null): boolean => {
  return user?.role === USER_ROLE.EMPLOYEE;
};

const USER_ROLE_LABELS: Record<UserRole, string> = {
  [USER_ROLE.ADMIN]: 'Platform Admin',
  [USER_ROLE.MANAGER]: 'Manager',
  [USER_ROLE.EMPLOYEE]: 'Employee',
};

/**
 * Human-readable label for a user role.
 *
 * The API types `role` as a nullable string, so this handles both a missing role
 * and a role the backend introduced before the frontend knew about it - the raw
 * value is shown rather than a blank cell.
 */
export const getUserRoleLabel = (role: UserRole | null): string =>
  role === null ? '—' : (USER_ROLE_LABELS[role] ?? role);

export type User = {
  id: ID;
  email: string;
  name: string;
  // The API declares both as nullable - see the /users/me response schema
  role: UserRole | null;
  isActive: boolean | null;
  rental?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  onboardingState?: Record<
    string,
    {
      status: 'completed' | 'skipped';
      updatedAt: string;
    }
  >;
  /**
   * Someone else set the password. Until the user changes it, every route but the
   * change-password ones answers 403 PASSWORD_CHANGE_REQUIRED.
   */
  mustChangePassword?: boolean;
  createdAt?: DateString;
  updatedAt?: DateString;
};

// Auth types
export type LoginCredentials = {
  email: string;
  password: string;
};

export type AuthToken = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
};

export type AuthUser = User & {
  token: AuthToken;
};

// Profile update types
export type UpdateUserProfile = {
  name: string;
  email: string;
};

export type ChangePassword = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};
